import Vue from 'vue'
import sessionStorageUtils from '@/lib/sessionStrage/session-strage-utils'
import { KumasanAuth } from '@/cognito/index'
import Cookies from 'js-cookie'

let ws: WebSocket | null = null
const ERROR_COUNT_LIMIT = 10
let errorCount = 0
const onMessageCallbacks: Map<string, (data: any) => void> = new Map()

// Vue.observable で state を reactive にする
const state = Vue.observable({ isConnected: false })
export const getIsConnected = () => {
  return state.isConnected
}

// 55分に一回のペースで呼び出す用のinterval
let refreshTokenIntervalId: number | null = null

/**
 * -------------------------------
 *  トークン有効期限チェック用関数
 * -------------------------------
 */
const checkAndRefreshTokenIfNeeded = async () => {
  const LIMIT_MINUTES = 5
  const refreshTokenExpireOfCookie = Cookies.get('refreshToken_expire')
  if (!refreshTokenExpireOfCookie) {
    window.alert(
      'cookieからトークンが取得できませんでした。\n 再接続できないため、ページを閉じます。'
    )
    window.close()
    return
  }

  const refreshTokenExpire = new Date(refreshTokenExpireOfCookie)
  const now = new Date()
  const diff = refreshTokenExpire.getTime() - now.getTime()
  const diffMinutes = Math.floor(diff / 1000 / 60)

  if (diffMinutes < LIMIT_MINUTES) {
    const kumasanInstance = new KumasanAuth()
    await kumasanInstance.getLatestIdTokenWithApi()
    console.log('[wsClient] Token refreshed.')
  }
}

/**
 * -------------------------------
 *  トークン自動チェックの開始・停止
 * -------------------------------
 */
const stopRefreshTokenTimer = () => {
  if (refreshTokenIntervalId !== null) {
    clearInterval(refreshTokenIntervalId)
    refreshTokenIntervalId = null
  }
}

// 55分に一回のペースで呼び出す
const TOKEN_CHECK_INTERVAL = 55 * 60 * 1000
const startRefreshTokenTimer = () => {
  stopRefreshTokenTimer()

  refreshTokenIntervalId = window.setInterval(async () => {
    await checkAndRefreshTokenIfNeeded()
  }, TOKEN_CHECK_INTERVAL)

  checkAndRefreshTokenIfNeeded()
}

/**
 * -------------------------------
 *  WebSocket初期化
 * -------------------------------
 */
export const initWebSocket = async (childId: string): Promise<void> => {
  if (state.isConnected) {
    return Promise.resolve()
  }

  const WS_ENDPOINT = process.env.VUE_APP_WS_URL
  const currentOffice = sessionStorageUtils.handleGetSessionStorage(
    'currentStaff'
  )
  const officeId = currentOffice.office_id
  const staffId = currentOffice.staff_id
  const accessToken = Cookies.get('kumasan_accessToken')

  return new Promise((resolve, reject) => {
    ws = new WebSocket(
      `${WS_ENDPOINT}?officeId=${officeId}&childId=${childId}&staffId=${staffId}&accessToken=${accessToken}`
    )

    ws.onopen = () => {
      console.log('[wsClient] WebSocket connected.')
      state.isConnected = true

      // WebSocketが接続されたらトークンの定期チェックを開始する
      startRefreshTokenTimer()

      resolve()
    }

    ws.onerror = e => {
      console.error('[wsClient] WebSocket error:', e)
      state.isConnected = false
      errorCount++

      if (errorCount < ERROR_COUNT_LIMIT) {
        return reject(e)
      }

      window.alert(
        'エラーが発生しました。再接続できないため、ページを閉じます。'
      )
      window.close()
      reject(e)
    }

    ws.onclose = () => {
      console.log('[wsClient] WebSocket closed.')
      state.isConnected = false

      stopRefreshTokenTimer()

      setTimeout(() => {
        initWebSocket(childId)
      }, 1000)
    }

    ws.onmessage = event => {
      try {
        const data = JSON.parse(event.data)
        onMessageCallbacks.forEach(cb => cb(data))
      } catch (err) {
        console.error('[wsClient] Failed to parse message:', err)
      }
    }
  })
}

/**
 * サーバへリクエストを送る関数
 */
export const sendWSRequest = async (data: any) => {
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    await initWebSocket(data.childId)
  }

  if (ws) {
    ws.send(JSON.stringify(data))
  } else {
    console.error('[wsClient] WebSocket is null.')
  }
}

/**
 * WebSocket を閉じる関数
 */
export const closeWebSocket = () => {
  if (ws) {
    ws.close()
    ws = null
  }
}

/**
 * リスナー解除
 */
export const removeOnMessageListenerWithKey = (key: string) => {
  if (onMessageCallbacks.has(key)) {
    onMessageCallbacks.delete(key)
  }
}

/**
 * リスナー登録（重複チェック付き）
 */
export const addOnMessageListenerWithKey = (
  key: string,
  callback: (data: any) => void
) => {
  // 同じ key のリスナーが既に登録されていれば削除
  if (onMessageCallbacks.has(key)) {
    removeOnMessageListenerWithKey(key)
  }
  // 新しいリスナーを登録
  onMessageCallbacks.set(key, callback)
}

/**
 * 一度だけ実行されるリスナーを登録する関数
 */
export const addOnMessageListenerOnce = (
  key: string,
  callback: (data: any) => void
) => {
  const onceCallback = (data: any) => {
    callback(data)
    removeOnMessageListenerWithKey(key)
  }
  addOnMessageListenerWithKey(key, onceCallback)
}
